// src/components/russia-map/RegionInfoPanel.tsx

import { Button } from "@/components/ui/button";
import { RegionInfo } from "@/lib/russiaRegions";

interface RegionData {
  leaderName?: string;
  phone?: string;
  tg?: string;
  birthDate?: string;
  appointDate?: string;
  legalDate?: string;
  deptDate?: string;
  volunteers?: number;
  studentTeams?: number;
  localDepts?: number;
  humanMission?: number;
  training?: number;
  emergency?: number;
  certified?: number;
  photoUrl?: string;
}

interface RegionInfoPanelProps {
  displayRegion: RegionInfo | null;
  regionData: Record<string, RegionData>;
  canEditRegion: boolean;
  selectedRegion: RegionInfo | null;
  onEditClick: () => void;
  formatDate: (date: any) => string;
}

export const RegionInfoPanel: React.FC<RegionInfoPanelProps> = ({
  displayRegion,
  regionData,
  canEditRegion,
  selectedRegion,
  onEditClick,
  formatDate,
}) => {
  if (!displayRegion) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-gray-400 text-center text-xs sm:text-sm md:text-base lg:text-lg px-4">
          Наведите на регион или кликните для просмотра деталей
        </p>
      </div>
    );
  }

  const data = regionData[displayRegion.isoCode] || {};

  return (
    <div className="space-y-4 overflow-y-auto flex-1 px-2 md:px-4 lg:px-6"> {/* Добавил padding responsive */}
      <div className="flex flex-col items-center">
        <div className="w-24 h-24 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 xl:w-36 xl:h-36 rounded-full bg-slate-800 border-4 border-purple-500 overflow-hidden shadow-xl">
          <img
            src={data.photoUrl || "https://placehold.co/200x200/333333/FFFFFF?text=Нет+фото"}
            alt="Руководитель"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = "https://placehold.co/200x200/333333/FFFFFF?text=Нет+фото";
            }}
          />
        </div>
        <h4 className="text-lg md:text-xl lg:text-2xl xl:text-3xl font-bold text-white mt-3 text-center leading-tight">
          {data.leaderName || "Не указано"}
        </h4>
        <p className="text-base md:text-lg lg:text-xl xl:text-2xl text-purple-300 text-center leading-normal">
          {displayRegion.name}
        </p>
      </div>

      <div className="space-y-1 text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl leading-normal"> {/* Улучшил line-height */}
        <div>
          <span className="text-purple-300">Округ:</span>{" "}
          <span className="text-white">{displayRegion.district || "—"}</span>
        </div>
        <div>
          <span className="text-purple-300">Руководитель:</span>{" "}
          <span className="text-white">{data.leaderName || "—"}</span>
        </div>
        <div>
          <span className="text-purple-300">Телефон:</span>{" "}
          <span className="text-white">{data.phone || "—"}</span>
        </div>
        <div>
          <span className="text-purple-300">Telegram:</span>{" "}
          {data.tg ? (
            <a
              href={`https://t.me/${data.tg.replace("@", "")}`}
              className="text-cyan-400 hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {data.tg}
            </a>
          ) : (
            "—"
          )}
        </div>
        <div>
          <span className="text-purple-300">Рождение:</span>{" "}
          <span className="text-white">{formatDate(data.birthDate)}</span>
        </div>
        <div>
          <span className="text-purple-300">Назначение:</span>{" "}
          <span className="text-white">{formatDate(data.appointDate)}</span>
        </div>
        <div>
          <span className="text-purple-300">Юр. лицо:</span>{" "}
          <span className="text-white">{formatDate(data.legalDate)}</span>
        </div>
        <div>
          <span className="text-purple-300">Отделение:</span>{" "}
          <span className="text-white">{formatDate(data.deptDate)}</span>
        </div>
        <div>
          <span className="text-purple-300">Добровольцев:</span>{" "}
          <span className="text-green-400 font-bold">{data.volunteers ?? 0}</span>
        </div>
        <div>
          <span className="text-purple-300">ССО:</span>{" "}
          <span className="text-purple-400 font-bold">{data.studentTeams ?? 0}</span>
        </div>
        <div>
          <span className="text-purple-300">МО:</span>{" "}
          <span className="text-purple-400 font-bold">{data.localDepts ?? 0}</span>
        </div>
        <div>
          <span className="text-purple-300">Гум. миссия:</span>{" "}
          <span className="text-yellow-400 font-bold">{data.humanMission ?? 0}</span>
        </div>
        <div>
          <span className="text-purple-300">ВЦПСФ:</span>{" "}
          <span className="text-blue-400 font-bold">{data.training ?? 0}</span>
        </div>
        <div>
          <span className="text-purple-300">ЧС:</span>{" "}
          <span className="text-red-400 font-bold">{data.emergency ?? 0}</span>
        </div>
        <div>
          <span className="text-purple-300">Аттестованных:</span>{" "}
          <span className="text-cyan-400 font-bold">{data.certified ?? 0}</span>
        </div>
      </div>

      {canEditRegion && selectedRegion && selectedRegion.isoCode === displayRegion.isoCode && (
        <Button
          size="sm"
          onClick={onEditClick}
          className="w-full text-sm md:text-base lg:text-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
        >
          Редактировать
        </Button>
      )}
    </div>
  );
};